/**
 * CCTP Configuration
 *
 * Shared configuration for Cross-Chain Transfer Protocol (CCTP).
 * Used by both frontend and backend.
 */

// Chain type
export type Chain = 'ethereum' | 'base' | 'optimism' | 'arbitrum' | 'polygon' | 'solana';

// EVM chain config
export interface EvmChainConfig {
  tokenMessenger: string;
  messageTransmitter: string;
  usdcToken: string;
  domain: number;
  chainId: number;
  rpcUrl?: string;
}

// Solana chain config
export interface SolanaChainConfig {
  tokenMessengerMinterProgram: string;
  messageTransmitterProgram: string;
  usdcMint: string;
  domain: number;
}

// Testnet configuration (CCTP V2 contract addresses — Sepolia / Devnet).
// `as const` (here and on MAINNET) preserves literal chainId types so switchChain(wagmiConfig,
// {chainId}) type-checks; CCTP_CONFIG below is the network-aware union (mirrors BRIDGE_CONTRACTS).
const TESTNET_CCTP_CONFIG = {
  ethereum: {
    // CCTP V2 contracts on Ethereum Sepolia
    tokenMessenger: '0x8fe6b999dc680ccfdd5bf7eb0974218be2542daa',
    messageTransmitter: '0xe737e5cebeeba77efe34d4aa090756590b1ce275',
    usdcToken: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',
    domain: 0,
    chainId: 11155111, // Sepolia
  },
  base: {
    // CCTP V2 contracts on Base Sepolia
    tokenMessenger: '0x8fe6b999dc680ccfdd5bf7eb0974218be2542daa',
    messageTransmitter: '0xe737e5cebeeba77efe34d4aa090756590b1ce275',
    usdcToken: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
    domain: 6,
    chainId: 84532, // Base Sepolia
  },
  optimism: {
    // CCTP V2 contracts on Optimism Sepolia (deterministic CREATE2)
    tokenMessenger: '0x8fe6b999dc680ccfdd5bf7eb0974218be2542daa',
    messageTransmitter: '0xe737e5cebeeba77efe34d4aa090756590b1ce275',
    usdcToken: '0x5fd84259d66Cd46123540766Be93DFE6D43130D7',
    domain: 2,
    chainId: 11155420, // Optimism Sepolia
  },
  arbitrum: {
    // CCTP V2 contracts on Arbitrum Sepolia (deterministic CREATE2)
    tokenMessenger: '0x8fe6b999dc680ccfdd5bf7eb0974218be2542daa',
    messageTransmitter: '0xe737e5cebeeba77efe34d4aa090756590b1ce275',
    usdcToken: '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d',
    domain: 3,
    chainId: 421614, // Arbitrum Sepolia
  },
  polygon: {
    // CCTP V2 contracts on Polygon Amoy (deterministic CREATE2)
    tokenMessenger: '0x8fe6b999dc680ccfdd5bf7eb0974218be2542daa',
    messageTransmitter: '0xe737e5cebeeba77efe34d4aa090756590b1ce275',
    usdcToken: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
    domain: 7,
    chainId: 80002, // Polygon Amoy
  },
  solana: {
    // CCTP V2 Program IDs (Devnet)
    // See: https://github.com/circlefin/solana-cctp-contracts
    tokenMessengerMinterProgram: 'CCTPV2vPZJS2u2BBsUoscuikbYjnpFmbFsvVuJdgUMQe',
    messageTransmitterProgram: 'CCTPV2Sm4AdWt5296sk4P66VBZ7bEhcARwFaaS9YPbeC',
    usdcMint: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU',
    domain: 5,
  },
} as const;

// Mainnet configuration — sourced VERBATIM from the verified backend config
// (apps/api/src/config/index.ts:252-291; "Verified: Circle CCTP V2 docs"). The CCTP V2
// tokenMessenger/messageTransmitter are deterministic CREATE2 — identical on every EVM chain.
const MAINNET_CCTP_CONFIG = {
  ethereum: {
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    usdcToken: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    domain: 0,
    chainId: 1,
  },
  base: {
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    usdcToken: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    domain: 6,
    chainId: 8453,
  },
  optimism: {
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    usdcToken: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
    domain: 2,
    chainId: 10,
  },
  // arbitrum/polygon: CCTP infra is live AND Parse FBV01 is now deployed (see MAINNET_BRIDGE_CONTRACTS),
  // so these USDC↔USDC corridors route via CCTP.
  arbitrum: {
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    usdcToken: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
    domain: 3,
    chainId: 42161,
  },
  polygon: {
    tokenMessenger: '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d',
    messageTransmitter: '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64',
    usdcToken: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    domain: 7,
    chainId: 137,
  },
  // Mainnet Solana CCTP V2 — sourced from Circle docs + VERIFIED on-chain 2026-06-27. MUST stay byte-identical to
  // the backend block (apps/api/src/config/index.ts CCTP_CONFIG.solana) — a drift = FE signs a different binding
  // than the operator. (CCTP V2 program ids are identical on mainnet + devnet; only the USDC mint differs.)
  solana: {
    tokenMessengerMinterProgram: 'CCTPV2vPZJS2u2BBsUoscuikbYjnpFmbFsvVuJdgUMQe',
    messageTransmitterProgram: 'CCTPV2Sm4AdWt5296sk4P66VBZ7bEhcARwFaaS9YPbeC',
    usdcMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    domain: 5,
  },
} as const;

/**
 * Compile-time global injected by Vite for the browser bundle.
 *
 * This file is shared between the Vite frontend AND the Node/ts-node backend. A bare
 * `import.meta.env.VITE_NETWORK` cannot live here: under the backend's CommonJS module
 * setting it is BOTH a TS1343 compile error and — because tsc emits `import.meta` verbatim
 * under `--module commonjs` — a runtime `SyntaxError: Cannot use 'import.meta' outside a
 * module` (a parse-time error a try/catch cannot trap). So the frontend injects the network
 * string through Vite `define` as `__PARSE_NETWORK__` (see apps/web/vite.config.ts), which
 * Vite statically replaces in the browser bundle. Under Node this identifier is undefined,
 * so the `typeof` guard falls through to `process.env`.
 */
declare const __PARSE_NETWORK__: string | undefined;

/**
 * Network selector (testnet vs mainnet).
 *
 * Resolution order:
 *   1. `process.env.VITE_NETWORK` / `process.env.NETWORK_ENV` — Node/ts-node (api, scripts).
 *      The api convention is `NETWORK_ENV` (see `apps/api/src/config/index.ts:9`).
 *   2. `__PARSE_NETWORK__` — Vite-injected from `import.meta.env.VITE_NETWORK` at build time
 *      (the frontend's established flag; see `apps/web/src/config/feeSplitter.ts`,
 *      `apps/web/src/config/wagmi.ts`). `process.env` is defined as `{}` in the browser
 *      bundle (apps/web/vite.config.ts), so step 1 is a no-op there and we reach this.
 *   3. Default 'testnet' — preserves current behaviour when nothing is set.
 *
 * Convention matches the api side: anything other than the exact string 'mainnet' ⇒ testnet.
 */
function resolveNetworkEnv(): string {
  // Node / ts-node path first.
  if (typeof process !== 'undefined' && process.env) {
    const fromProcess = process.env.VITE_NETWORK || process.env.NETWORK_ENV;
    if (fromProcess) return fromProcess;
  }
  // Vite browser path — `__PARSE_NETWORK__` is replaced statically by Vite; `undefined`
  // under Node (guarded by typeof so referencing it never throws a ReferenceError).
  if (typeof __PARSE_NETWORK__ !== 'undefined' && __PARSE_NETWORK__) {
    return __PARSE_NETWORK__;
  }
  return 'testnet';
}

/** True when running against testnet (default). Mirrors the api `!== 'mainnet'` convention. */
export const IS_TESTNET: boolean = resolveNetworkEnv() !== 'mainnet';

/**
 * Active CCTP config for the resolved network — network-aware, mirroring BRIDGE_CONTRACTS below.
 * Previously this was a single hard-coded TESTNET object, so on mainnet it fed testnet chainId / USDC /
 * tokenMessenger / messageTransmitter into the burn path (useBurn.ts) — the wrong contracts. Now it
 * switches on IS_TESTNET exactly like BRIDGE_CONTRACTS and the backend CCTP_CONFIG.
 */
export const CCTP_CONFIG = IS_TESTNET ? TESTNET_CCTP_CONFIG : MAINNET_CCTP_CONFIG;

/**
 * ParseCctpBridge contract addresses, per network.
 *
 * These contracts wrap Circle's CCTP with fee collection. `BRIDGE_CONTRACTS` below resolves
 * to the active network's record, so all existing consumers (getBridgeContract,
 * isBridgeContractDeployed, useBurn.ts) keep working against the same flat shape.
 *
 * IMPORTANT: Update these addresses after deploying contracts to each network.
 */
export const TESTNET_BRIDGE_CONTRACTS: Record<Exclude<Chain, 'solana'>, string> = {
  // All five chains are now FBV01 V9-1, freshly deployed 2026-06-29 from current source
  // (codesize 11835B == mainnet V9-1; selector 132420f6 present, referrer-variant 13fadc43 absent;
  // operator/admin/pauser = 0x7d77, treasuryA = 0xd81e, treasuryB = 0x32aa). Each chain has its OWN
  // distinct address — this also corrects the prior copy-paste where polygon == optimism.
  // Ethereum Sepolia - FBV01 V9-1
  ethereum: '0xd475367B8DC83461Aa4bE6aA489E1AD50593f448',
  // Base Sepolia - FBV01 V9-1 (the working template; deployed 2026-06-30)
  base: '0x692f5c5D45AE38383591806Fb12a668AE6b2372C',
  // Optimism Sepolia - FBV01 V9-1
  optimism: '0x09784e482eB7EA0BC32F8dbadBCfF63be490EC78',
  // Arbitrum Sepolia - FBV01 V9-1
  arbitrum: '0xfdB2294A1935ba3c65B40F01fd4abE64E1e6EdB6',
  // Polygon Amoy - FBV01 V9-1. NB: same address as the Arbitrum MAINNET FeeSplitter — a
  // same-deployer (0x7d77) / same-nonce CREATE collision across chains, NOT a copy-paste. Do not "correct".
  polygon: '0x8175C8acD1c4fD8B2550E668c716E90CCE481a34',
};

export const MAINNET_BRIDGE_CONTRACTS: Record<Exclude<Chain, 'solana'>, string> = {
  // Ethereum mainnet - FBV01 dual-cap (deployed 2026-07-09, code 12229B, absFeeExemption=150000, source-verified)
  ethereum: '0xdf0a64ec08b32a7f689c33777b713af3c727039f',
  // Base mainnet - FBV01 dual-cap (deployed 2026-07-09, code 12229B)
  base: '0x31d91bd3ead0c10ac3e3cd9acfc9a403c205017a',
  // Optimism mainnet - FBV01 dual-cap (deployed 2026-07-09, code 12229B)
  optimism: '0xfa142499d8429b8fd0d2fea87c329cf3db81ca93',
  // Arbitrum mainnet - FBV01 dual-cap (deployed 2026-07-09, code 12229B)
  arbitrum: '0xa1be0367362fc4adb54562be8affb7add55059c2',
  // Polygon mainnet - FBV01 dual-cap (deployed 2026-07-09, code 12229B)
  polygon: '0xf4dadfffe2478517390d27e02c71278e7e9ee69c',
};
// OLD V9-1 set (now to be paused; keep for the record / rollback):
//   eth 0x208139d6481419e4454782fe9cb118ba67812f14 · base 0xc600e1ed1f00085a58550265d6ec832ecb349237
//   op 0xaa35414200efc3a4fb687597ca444e3f80405966 · arb 0x7afef0e10f3ade07f94189e7a02e1d800b7bb923
//   poly 0xdd21d54665f5f7fca5887688b9f64e606bdba657

/**
 * Active ParseCctpBridge contract addresses for the resolved network.
 * Keeps the historical flat `Record<Exclude<Chain,'solana'>, string>` shape so consumers
 * (getBridgeContract, isBridgeContractDeployed, useBurn.ts) need no changes.
 */
export const BRIDGE_CONTRACTS: Record<Exclude<Chain, 'solana'>, string> =
  IS_TESTNET ? TESTNET_BRIDGE_CONTRACTS : MAINNET_BRIDGE_CONTRACTS;

/**
 * Solana FBV01 bridge program addresses
 * Program wraps Circle's CCTP with operator-signed fee quotes
 */
// Network-aware fbv02 (Parse Solana bridge) config. Devnet = `J3Y5…` (fresh redeploy 2026-06-29, admin/pauser/upgrade-auth = BdMU we control); mainnet = `Dt5N39…` (deployed + initialized
// 2026-06-27; operator + treasuries verified on-chain). The runner injects the program id via BRIDGE_PROGRAM_SOLANA
// and overrides the IDL address; the FE reads it here. The mainnet ALT `Buq5fXKN…` was created 2026-06-28 (25
// canonical entries; the 2 prerequisite bridge/treasuryB USDC ATAs created in the same bring-up) — keep it in sync
// with SOLANA_ALT_ADDRESS in .env.mainnet.
export const SOLANA_BRIDGE_CONFIG = IS_TESTNET ? {
  programId: 'J3Y5P5ee7CvTYh8mopZUtRGbSLS1AaoAuUBG6TNDxB7B',
  altAddress: 'DjesA5kEXstr5U8ogrYkCqE11LGahYqAfuFRwPboVPB6',
  treasuryA: '6PpoJvNSh4WCni1xU9J1HXRJ3xFGzHbusVe5CjBgknM2',
  treasuryB: '8eqZgYxUDXdihYvgS9bsLDBCLvMBdSaTxaV1z2gHLSJd',
} : {
  programId: 'Dt5N39YU7Rbq6FFNYEV6vjZejs3VoDLLMAa5jWf5VCHX',
  altAddress: 'Buq5fXKNoM9rjLLDHesBMx2q7vt5NbfrFHx4Q6RKyLyB',
  // treasuryA rotated 2026-07-16 (6Ppo → 2sLoup, Ledger cold). MUST stay == on-chain state.treasury_a
  // (Dt5N `set_treasuries` → 2sLoup). The FE (useBurn.ts:863) + backend burn-builder pass this as the
  // `treasury_a` account and Anchor enforces `treasury_a == state.treasury_a` (lib.rs:318) — so ship
  // the clients with the new value whenever treasury_a rotates (clients first, then flip on-chain).
  treasuryA: '2sLoupF3rGWuD1X6i7QknsJ3uhGvqzSP4f4dK6zvFqxx',
  // treasuryB moved to KMS 2026-07-16 (Duym5 → FNRZ, fresh KMS refill signer c6fb7c49; Phantom→KMS,
  // unifies fee-dest/refill/autoswap). MUST stay == on-chain state.treasury_b (Dt5N set_treasuries → FNRZ).
  // The FE (useBurn.ts) + backend burn-builder pass this as the `treasury_b` account; Anchor enforces
  // treasury_b == state.treasury_b, so this literal + on-chain flip together (clients ship FIRST, then on-chain).
  //
  // ✅ WHAT PROD/EC2 POINTS TO (re-verified on-chain 2026-07-17 via public RPC): mainnet Dt5N
  // state.treasury_a/b == 2sLoup / FNRZ (paused=false) — COHERENT with these literals, so Solana→EVM burns
  // are fine. EC2/prod uses SSM + THIS literal (FNRZ) for the burn ix. NOTE: the LOCAL .env.mainnet still
  // reads TREASURY_B_SOLANA_ADDRESS=Duym5 (pre-rotation Move-A) — kept as-is on purpose, and HARMLESS: that
  // var feeds ONLY the treasury-bot fee-recipient (config/index.ts:75), never the burn ix. Do NOT run the
  // stale apps/api/src/scripts/treasury-b-revert-solana.ts (6Ppo/8eqZ, superseded; inert guard).
  treasuryB: 'FNRZuCC2hzJtcrt45F1V8YKz9HeBJYZ2sNkTVmgyaxjo',
};

/**
 * Check if bridge contracts are deployed (non-zero addresses)
 */
export function isBridgeContractDeployed(chain: Exclude<Chain, 'solana'>): boolean {
  const address = BRIDGE_CONTRACTS[chain];
  return !!address && address !== '0x0000000000000000000000000000000000000000';
}

// Domain mapping
export const CHAIN_DOMAINS: Record<Chain, number> = {
  ethereum: 0,
  base: 6,
  optimism: 2,
  arbitrum: 3,
  polygon: 7,
  solana: 5,
};

// Minimal ABIs for frontend
export const ERC20_ABI = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function balanceOf(address account) view returns (uint256)',
];

// CCTP V2 TokenMessengerV2 ABI
// V2 adds: destinationCaller, maxFee, minFinalityThreshold
// See: https://github.com/circlefin/evm-cctp-contracts/blob/master/src/v2/TokenMessengerV2.sol
export const TOKEN_MESSENGER_ABI = [
  'function depositForBurn(uint256 amount, uint32 destinationDomain, bytes32 mintRecipient, address burnToken, bytes32 destinationCaller, uint256 maxFee, uint32 minFinalityThreshold) external',
  'function getMinFeeAmount(uint256 amount) external view returns (uint256)',
];

/**
 * ParseCctpBridge ABI
 * Bridge contract that wraps CCTP with operator-signed fee quotes
 *
 * The Quote struct matches the contract:
 * struct Quote {
 *   uint32 destDomain;
 *   uint256 networkFeeUsdc;
 *   uint256 networkFeeNative;
 *   uint256 percentageFeeBps;
 *   uint256 timestamp;
 *   uint256 nonce;
 *   address userAddress;
 *   uint256 maxFee;
 *   uint32 minFinalityThreshold;
 *   address referrerAddress;
 *   uint16 referrerShareBps;
 * }
 */
export const PARSE_BRIDGE_ABI = [
  // Core bridge function
  'function bridge(uint256 amount, bytes32 recipient, tuple(uint32 destDomain, uint256 networkFeeUsdc, uint256 networkFeeNative, uint256 percentageFeeBps, uint256 timestamp, uint256 nonce, address userAddress, uint256 maxFee, uint32 minFinalityThreshold, address referrerAddress, uint16 referrerShareBps) quote, bytes signature, bool payNative) external payable',
  // Approval-free bridge using EIP-3009 receiveWithAuthorization
  // V9-1: the EIP-3009 auth nonce is NOT a calldata field — it is recomputed on-chain via
  // _authBindingHash(recipient, destDomain, payNative, quote.nonce). The auth tuple drops
  // `bytes32 nonce` accordingly; supplying it would no longer match the struct.
  'function bridgeWithAuth(uint256 amount, bytes32 recipient, tuple(uint32 destDomain, uint256 networkFeeUsdc, uint256 networkFeeNative, uint256 percentageFeeBps, uint256 timestamp, uint256 nonce, address userAddress, uint256 maxFee, uint32 minFinalityThreshold, address referrerAddress, uint16 referrerShareBps) quote, bytes operatorSignature, bool payNative, tuple(uint256 validAfter, uint256 validBefore, uint8 v, bytes32 r, bytes32 s) auth) external payable',
  // View functions
  'function isNonceUsed(address user, uint256 nonce) external view returns (bool)',
  'function getNextNonce(address user) external view returns (uint256)',
  'function getConfig() external view returns (address operator, address admin, address treasuryA, address treasuryB, bool paused)',
  // Constants
  'function MAX_FEE_BPS() external view returns (uint256)',
  'function QUOTE_VALIDITY() external view returns (uint256)',
  'function MIN_AMOUNT() external view returns (uint256)',
];

/**
 * Quote data structure for ParseCctpBridge
 * This interface matches the Quote struct in the contract
 */
export interface BridgeQuote {
  destDomain: number;
  networkFeeUsdc: string;
  networkFeeNative: string;
  percentageFeeBps: number;
  timestamp: number;
  nonce: number;
  userAddress: string;
  maxFee: string;
  minFinalityThreshold: number;
  /** Referrer address — EVM address or Solana pubkey (optional, present when referrer active) */
  referrerAddress?: string;
  /** Referrer share of protocol fee in basis points (optional, 0 when no referrer) */
  referrerShareBps?: number;
}

// Helper functions
export function getChainConfig(chain: Chain) {
  return CCTP_CONFIG[chain];
}

export function getDomainForChain(chain: Chain): number {
  return CHAIN_DOMAINS[chain];
}

export function isEvmChain(chain: Chain): boolean {
  return chain === 'ethereum' || chain === 'base' || chain === 'optimism' || chain === 'arbitrum' || chain === 'polygon';
}

export function isSolanaChain(chain: Chain): boolean {
  return chain === 'solana';
}

export function getChainName(chain: Chain): string {
  const names: Record<Chain, string> = {
    ethereum: 'Ethereum Sepolia',
    base: 'Base Sepolia',
    optimism: 'Optimism Sepolia',
    arbitrum: 'Arbitrum Sepolia',
    polygon: 'Polygon Amoy',
    solana: 'Solana Devnet',
  };
  return names[chain];
}
